import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationService } from '@gt/_services/authentication.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ParaAssessmentContextService } from '@para/shared/services/para-assessment-context.service';
import { ParaAssessmentSeriesService, type ParaAssessmentSeriesItemDto } from '@para/shared/services/para-assessment-series.service';
import { ParaSelectedAssessmentService } from '@para/shared/services/para-selected-assessment.service';
import { getAssessmentThemeColor, getAssessmentCardBackgroundImage } from '@para/shared/utils/para-assessment-theme.util';
import { getParaRoleDisplayLabel } from '@para/shared/utils/para-roles.util';
import { syncParaScreenshotRedactDocumentClass } from '@para/shared/utils/para-screenshot-redact.util';

@Component({
	selector: 'app-para-shared-layout',
	templateUrl: './para-shared-layout.component.html',
	styleUrls: ['./para-shared-layout.component.css'],
})
export class ParaSharedLayoutComponent implements OnInit, OnDestroy {
	public pageTitle = '';
	public userName = '';
	public userInitials = 'U';
	public role = '';
	public roleLabel = '';
	public seriesItem: ParaAssessmentSeriesItemDto | null = null;
	public seriesItems: ParaAssessmentSeriesItemDto[] = [];
	public seriesUnlockLabel: string | null = null;
	public assessmentCardGradient = getAssessmentCardBackgroundImage('para-ce-screener');
	/** Resolved from assessment context API (long label preferred). */
	private assessmentHeading: string | null = null;
	private subscriptions: Subscription[] = [];

	constructor(
		public authenticationService: AuthenticationService,
		private jwtHelper: JwtHelperService,
		private router: Router,
		private readonly paraAssessmentContext: ParaAssessmentContextService,
		private readonly paraSeries: ParaAssessmentSeriesService,
		private readonly paraSelectedAssessment: ParaSelectedAssessmentService
	) {}

	ngOnInit(): void {
		syncParaScreenshotRedactDocumentClass();
		this.syncPageTitle();
		this.loadUserInfo();
		this.subscriptions.push(
			this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
				syncParaScreenshotRedactDocumentClass();
				this.syncPageTitle();
			})
		);
		this.subscriptions.push(
			this.paraAssessmentContext.selectedNodeAssessmentContext$.subscribe((ctx) => {
				const a = ctx?.assessment;
				const longLabel = a?.longLabel != null ? String(a.longLabel).trim() : '';
				const shortLabel = a?.shortLabel != null ? String(a.shortLabel).trim() : '';
				this.assessmentHeading = longLabel || shortLabel || null;
				this.syncPageTitle();
			})
		);
		this.subscriptions.push(
			this.paraSeries.selectedNodeAssessmentSeries$.subscribe((s) => {
				this.seriesItems = s?.series ?? [];
			})
		);
		this.subscriptions.push(
			this.paraSelectedAssessment.activeSeriesItem$.subscribe((item) => {
				this.seriesItem = item;
			})
		);
		this.subscriptions.push(
			this.paraSelectedAssessment.unlockLabel$.subscribe((label) => {
				this.seriesUnlockLabel = label;
			})
		);
		this.subscriptions.push(
			this.paraSelectedAssessment.selectedAssessmentId$.subscribe((id) => {
				this.assessmentCardGradient = getAssessmentCardBackgroundImage(id || '');
			})
		);
	}

	public selectAssessment(item: ParaAssessmentSeriesItemDto): void {
		if (!item?.assessmentId) return;
		// Locked items can still be selected for read-only viewing.
		this.paraSelectedAssessment.select(String(item.assessmentId));
	}

	public getAssessmentColor(id: string | undefined): string {
		return getAssessmentThemeColor(id || '');
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	get pageTitleLoading(): boolean {
		return !this.pageTitle?.trim();
	}

	private syncPageTitle(): void {
		this.pageTitle = this.assessmentHeading?.trim() || '';
	}

	private loadUserInfo(): void {
		if (!this.authenticationService.isAuthenticated()) return;
		const token = localStorage.getItem('currentUser');
		if (!token) return;
		const decoded: any = this.jwtHelper.decodeToken(token);
		this.role = decoded?.role || '';
		this.roleLabel = getParaRoleDisplayLabel(this.role);
		const user = decoded?.user;
		if (user) {
			this.userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
			const firstInitial = user.firstName ? String(user.firstName).charAt(0).toUpperCase() : '';
			const lastInitial = user.lastName ? String(user.lastName).charAt(0).toUpperCase() : '';
			this.userInitials = `${firstInitial}${lastInitial}` || 'U';
		} else {
			this.userName = 'User';
			this.userInitials = 'U';
		}
	}

	public logout(): void {
		this.authenticationService.logout();
	}
}
